'use client'

import { useState, useEffect } from 'react'
import { createClient } from '@/lib/supabase/browser'
import { useSiteSettings } from '@/lib/useSiteSettings'
import { MEMBERS_ENABLED } from '@/lib/featureFlags'

/**
 * FavoriteButton — 愛心收藏按鈕，絕對定位在 ArtworkCard 左上角。
 *
 * 行為：
 *  - 未登入：不 render（避免 push CTA 到 /login，尊重 browse without auth）
 *  - 登入：讀 favorites 表是否已收藏，toggle add/remove
 *  - stopPropagation 避免觸發 card 的 click（打開 quick view）
 */
export default function FavoriteButton(props) {
  // Flag gate. Wrapper keeps the inner component's hooks from running when the
  // whole member system is disabled — satisfies the rules-of-hooks lint rule.
  if (!MEMBERS_ENABLED) return null
  return <FavoriteButtonInner {...props} />
}

function FavoriteButtonInner({ artworkId }) {
  const [favorited, setFavorited] = useState(false)
  const [loggedIn, setLoggedIn] = useState(false)
  const [loading, setLoading] = useState(false)
  // Phase 3.10 — favorite labels from CMS
  const { settings: b } = useSiteSettings('brand')

  useEffect(() => {
    const supabase = createClient()
    let cancelled = false

    supabase.auth.getUser().then(({ data }) => {
      if (cancelled) return
      if (!data.user) {
        setLoggedIn(false)
        return
      }
      setLoggedIn(true)
      supabase
        .from('favorites')
        .select('artwork_id')
        .eq('artwork_id', artworkId)
        .maybeSingle()
        .then(({ data: fav }) => {
          if (!cancelled) setFavorited(!!fav)
        })
    })

    return () => {
      cancelled = true
    }
  }, [artworkId])

  if (!loggedIn) return null

  const toggle = async (e) => {
    e.stopPropagation()
    if (loading) return
    setLoading(true)
    try {
      if (favorited) {
        await fetch(`/api/profile/favorites/${encodeURIComponent(artworkId)}`, {
          method: 'DELETE',
        })
        setFavorited(false)
      } else {
        await fetch('/api/profile/favorites', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ artworkId }),
        })
        setFavorited(true)
      }
    } catch {
      // Silent fail — next click retries. No user-facing toast infra yet.
    } finally {
      setLoading(false)
    }
  }

  return (
    <button
      type="button"
      onClick={toggle}
      onKeyDown={(e) => e.stopPropagation()}
      disabled={loading}
      aria-label={favorited ? (b.favorite_remove_label || '取消收藏') : (b.favorite_add_label || '加入收藏')}
      aria-pressed={favorited}
      className="absolute top-3 left-3 w-10 h-10 flex items-center justify-center rounded-full transition-transform active:scale-90"
      style={{ backgroundColor: 'rgba(0,0,0,0.4)', color: '#fff', backdropFilter: 'blur(4px)' }}
    >
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill={favorited ? '#e85d75' : 'none'}
        stroke={favorited ? '#e85d75' : 'currentColor'}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
      </svg>
    </button>
  )
}
